import { Link, useParams } from 'react-router-dom';
import { findSubService } from '@/data/services';
import { Seo, breadcrumbJsonLd, serviceJsonLd } from '@/lib/seo';
import { Hero } from '@/components/sections/Hero';
import { CTABand } from '@/components/sections/CTABand';
import { QuoteForm } from '@/components/forms/QuoteForm';
import { ArrowRight, ChevronRight } from '@/components/ui/Icons';
import { company } from '@/data/company';
import NotFound from '@/pages/NotFound';

export default function SubServicePage() {
  const { service: serviceSlug, sub: subSlug } = useParams();
  if (!serviceSlug || !subSlug) return <NotFound />;
  const found = findSubService(serviceSlug, subSlug);
  if (!found) return <NotFound />;
  const { service, sub } = found;
  const path = `/${service.slug}/${sub.slug}`;
  const subImage = `/images/services/${sub.slug}.jpg`;
  const description =
    `${sub.short} Specialist ${sub.title.toLowerCase()} from ${company.legalName} across ${company.serviceCoverage}. Free surveys, no call-out charges.`;

  return (
    <>
      <Seo
        title={`${sub.title} in Essex, London & Suffolk | ${service.navLabel}`}
        description={description}
        path={path}
        image={subImage}
        imageAlt={`${sub.title} - ${service.navLabel} by ${company.legalName}`}
        keywords={`${sub.title}, ${sub.title} Essex, ${sub.title} London, ${sub.title} Suffolk, ${service.navLabel} ${company.shortName}`}
        jsonLd={[
          serviceJsonLd(`${sub.title} (${service.navLabel})`, path, description, { image: subImage }),
          breadcrumbJsonLd([
            { name: 'Home', path: '/' },
            { name: service.navLabel, path: `/${service.slug}` },
            { name: sub.title, path },
          ]),
        ]}
      />

      <Hero
        eyebrow={service.navLabel}
        title={sub.title}
        subtitle={sub.short}
        imageSrc={subImage}
      />

      <nav aria-label="Breadcrumb" className="bg-paper-50">
        <div className="container-page py-3 text-sm text-ink-700">
          <ol className="flex flex-wrap items-center gap-1">
            <li><Link to="/" className="hover:text-navy-700">Home</Link></li>
            <li className="px-1 text-gold-500"><ChevronRight className="h-3 w-3" /></li>
            <li><Link to={`/${service.slug}`} className="hover:text-navy-700">{service.navLabel}</Link></li>
            <li className="px-1 text-gold-500"><ChevronRight className="h-3 w-3" /></li>
            <li className="font-semibold text-navy-800">{sub.title}</li>
          </ol>
        </div>
      </nav>

      <section className="container-page py-16 sm:py-20">
        <div className="grid gap-10 lg:grid-cols-[1.4fr_1fr]">
          <div className="prose-bliss">
            <h2 className="text-3xl">{sub.title}</h2>
            <p>{sub.short}</p>
            {sub.body?.map((p) => <p key={p}>{p}</p>) ?? (
              <>
                <p>
                  Our team has years of experience delivering {sub.title.toLowerCase()}{' '}
                  across {service.navLabel.toLowerCase()} jobs in {company.serviceCoverage}.
                  Every job starts with a free, no-obligation site survey so we can quote you
                  honestly and accurately.
                </p>
                <p>
                  Whether it’s a one-off repair or part of a larger {service.navLabel.toLowerCase()}{' '}
                  project, we use the right materials, finish to a high standard and back our
                  work with a written guarantee. We work across {company.serviceTowns.join(', ')} and
                  the surrounding areas.
                </p>
              </>
            )}
            <h3>Other {service.navLabel}</h3>
            <ul className="chevron-list space-y-2">
              {service.subservices
                .filter((s) => s.slug !== sub.slug)
                .map((s) => (
                  <li key={s.slug}>
                    <Link to={`/${service.slug}/${s.slug}`} className="text-navy-800 hover:text-gold-600">
                      {s.title}
                    </Link>
                  </li>
                ))}
            </ul>
            <p>
              <Link to={`/${service.slug}`} className="inline-flex items-center gap-1 font-semibold text-gold-700 hover:text-gold-600">
                See all {service.navLabel} <ArrowRight className="h-4 w-4" />
              </Link>
            </p>
          </div>

          <div className="self-start">
            <QuoteForm defaultService={`${service.slug}/${sub.slug}`} />
          </div>
        </div>
      </section>

      <CTABand />
    </>
  );
}
